import { useCallback, useEffect, useMemo, useRef, FC } from 'react';
import intl from 'react-intl-universal';
import { useSelector } from 'react-redux';
import { AppliedFilters } from '@getsynapse/design-system';
import { PROJECT_HEALTH, SETTINGS_FILTERS } from 'utils/constants';
import {
  ProjectStatus,
  ProjectResourcingType,
  ProjectPrivacy,
  ProjectPriority,
  ProjectHealth,
  ProjectProcess,
  ProcessStage,
} from 'utils/types/projectsList';
import { AppliedFilter, ProjectCategory } from 'utils/customTypes';
import { DateRangeFilter, ProjectsListFilters } from 'types/store/projectsList';
import { selectSelectedProjectCategories } from 'state/ProjectsList/filters/Categories/categoriesSlice';
import useProjectsListSidePanelFilters from 'Pages/ProjectsListPage/hooks/useProjectsListSidePanelFilters/useProjectsListSidePanelFilters';
import useProjectOwners from 'Pages/ProjectsListPage/hooks/useProjectOwners/useProjectOwners';
import { useProjectTeams } from 'Pages/ProjectsListPage/hooks/useProjectTeams/useProjectTeams';
import { useProjectBusinessUnits } from 'Pages/ProjectsListPage/hooks/useProjectBusinessUnits/useProjectBusinessUnits';
import { useProjectProcesses } from 'Pages/ProjectsListPage/hooks/useProjectProcesses/useProjectProcesses';
import { useProjectStages } from 'Pages/ProjectsListPage/hooks/useProjectStages/useProjectStages';
import { getDateRangeFilterLabel } from 'Pages/ProjectsListPage/components/ActiveProjects/components/AppliedProjectsTableFilters/helpers';
import { useProjectPrograms } from 'Pages/ProjectsListPage/hooks/useProjectPrograms/useProjectPrograms';

interface AppliedProjectsTableFiltersProps {
  isConsolidatedView?: boolean;
  tableName?: string;
}

const AppliedProjectsTableFilters: FC<AppliedProjectsTableFiltersProps> = ({
  isConsolidatedView = false,
  tableName = 'active-projects-table',
}) => {
  const { appliedFilters, applyFilters, clearFilters } =
    useProjectsListSidePanelFilters({
      isConsolidatedView,
      filterTypeKey: isConsolidatedView
        ? SETTINGS_FILTERS.CONSOLIDATED_PROJECTS_TABLE
        : SETTINGS_FILTERS.ACTIVE_PROJECTS_TABLE,
    });

  const {
    fetchSelectedProjectOwners,
    selectedProjectOwners,
    selectedProjectOwnerIds,
  } = useProjectOwners();

  const { fetchSelectedLdTeams, selectedLdTeams, shouldFetchLdTeams } =
    useProjectTeams({
      appliedFilters: appliedFilters.teams as string[],
    });

  const {
    fetchSelectedBusinessTeams,
    selectedBusinessTeams,
    shouldFetchBusinessTeams,
  } = useProjectBusinessUnits({
    appliedFilters: appliedFilters.businessTeams as string[],
  });

  const { fetchSelectedProcesses, selectedProcesses, shouldFetchProcesses } =
    useProjectProcesses({
      appliedFilters: appliedFilters.process as string[],
    });

  const { fetchSelectedStages, selectedStages, shouldFetchStages } =
    useProjectStages({
      appliedFilters: appliedFilters.stage as string[],
    });

  const { programs, fetchProjectPrograms } = useProjectPrograms();
  const lastProgramsFilterFetchRef = useRef<string | null>(null);

  const selectedCategories = useSelector(selectSelectedProjectCategories);

  const shouldFetchProjectOwners = useMemo(() => {
    if (
      appliedFilters.owners &&
      Array.isArray(appliedFilters.owners) &&
      appliedFilters.owners.length > 0
    ) {
      return (
        selectedProjectOwners.length === 0 ||
        appliedFilters.owners.length !== selectedProjectOwnerIds.length
      );
    }
  }, [
    appliedFilters.owners,
    selectedProjectOwnerIds.length,
    selectedProjectOwners.length,
  ]);

  const appliedProjectOwnersFilter = useMemo(() => {
    return selectedProjectOwners.map(
      (owner) =>
        ({
          key: 'owners',
          value: owner.id,
          label: owner.fullName,
        } as AppliedFilter)
    );
  }, [selectedProjectOwners]);

  const appliedStatusFilter = useMemo(() => {
    if (!appliedFilters.status) return [];
    return (appliedFilters.status as ProjectStatus[]).map((status) => ({
      key: 'status',
      value: status,
      label: intl.get(`PROJECT_DETAIL.STATUS_OPTIONS.${status.toUpperCase()}`),
    }));
  }, [appliedFilters.status]);

  const appliedLdTeamsFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    return selectedLdTeams.map((team) => ({
      key: 'teams',
      value: team.id,
      label: team.name,
    }));
  }, [isConsolidatedView, selectedLdTeams]);

  const appliedBusinessTeamsFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    return selectedBusinessTeams.map((team) => ({
      key: 'businessTeams',
      value: team.id,
      label: team.name,
    }));
  }, [isConsolidatedView, selectedBusinessTeams]);

  const appliedProgramsFilter = useMemo(() => {
    if (!isConsolidatedView || !Array.isArray(appliedFilters.programs)) {
      return [];
    }

    return appliedFilters.programs.map((programId) => ({
      key: 'programs',
      value: programId,
      label:
        programs.find((program) => program.id === programId)?.title ??
        programId,
    }));
  }, [appliedFilters.programs, isConsolidatedView, programs]);

  const appliedWithoutProgramsFilter = useMemo(() => {
    if (!isConsolidatedView || appliedFilters.withoutPrograms !== true) {
      return [];
    }

    return [
      {
        key: 'withoutPrograms',
        value: 'true',
        label: intl.get('PROJECTS_LIST_PAGE.FILTERS.WITHOUT_PROGRAMS'),
      },
    ];
  }, [appliedFilters.withoutPrograms, isConsolidatedView]);

  // `fetchTeamPrograms` excludes archived/deleted programs, so a persisted
  // filter that references one keeps a per-ID "missing" check true forever and
  // spams the endpoint on every effect re-fire. Once we've fetched the full
  // list once, IDs that aren't in it are deleted - the applied-chip mapping
  // already falls back to the raw ID.
  const shouldFetchPrograms = useMemo(() => {
    if (
      !Array.isArray(appliedFilters.programs) ||
      appliedFilters.programs.length === 0
    ) {
      return false;
    }

    return programs.length === 0;
  }, [appliedFilters.programs, programs.length]);

  const programsFilterKey = useMemo(() => {
    if (!Array.isArray(appliedFilters.programs)) {
      return '';
    }

    return JSON.stringify([...appliedFilters.programs].sort());
  }, [appliedFilters.programs]);

  const appliedDateRangeFilters = useMemo(() => {
    const filters: AppliedFilter[] = [];
    if (appliedFilters.startDate) {
      const startDate = appliedFilters.startDate as DateRangeFilter;
      if (startDate.min || startDate.max) {
        filters.push({
          key: 'startDate',
          value: JSON.stringify(startDate),
          label: getDateRangeFilterLabel(startDate),
        });
      }
    }
    if (appliedFilters.targetCompletionDate) {
      const targetCompletionDate =
        appliedFilters.targetCompletionDate as DateRangeFilter;
      if (targetCompletionDate.min || targetCompletionDate.max) {
        filters.push({
          key: 'targetCompletionDate',
          value: JSON.stringify(targetCompletionDate),
          label: getDateRangeFilterLabel(targetCompletionDate),
        });
      }
    }
    if (appliedFilters.actualCompletionDate) {
      const actualCompletionDate =
        appliedFilters.actualCompletionDate as DateRangeFilter;
      if (actualCompletionDate.min || actualCompletionDate.max) {
        filters.push({
          key: 'actualCompletionDate',
          value: JSON.stringify(actualCompletionDate),
          label: getDateRangeFilterLabel(actualCompletionDate),
        });
      }
    }
    return filters;
  }, [appliedFilters]);

  const appliedResourcingTypeFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.resourcingType) return [];
    return (appliedFilters.resourcingType as ProjectResourcingType[]).map(
      (resourcingType) => ({
        key: 'resourcingType',
        value: resourcingType,
        label: intl.get(
          `PROJECT_DETAIL.RESOURCING_TYPE_OPTIONS.${resourcingType.toUpperCase()}`
        ),
      })
    );
  }, [appliedFilters.resourcingType, isConsolidatedView]);

  const appliedPrivacyFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.privacy) return [];
    return (appliedFilters.privacy as ProjectPrivacy[]).map((privacy) => ({
      key: 'privacy',
      value: privacy,
      label: intl.get(
        `PROJECT_DETAIL.PRIVACY_OPTIONS.${privacy.toUpperCase()}`
      ),
    }));
  }, [appliedFilters.privacy, isConsolidatedView]);

  const appliedPriorityFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.priority) return [];
    return (appliedFilters.priority as ProjectPriority[]).map((priority) => ({
      key: 'priority',
      value: priority,
      label: intl.get(
        `PROJECT_DETAIL.PRIORITY_OPTIONS.${priority.toUpperCase()}`
      ),
    }));
  }, [appliedFilters.priority, isConsolidatedView]);

  const appliedHealthFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.health) return [];
    return (appliedFilters.health as ProjectHealth[]).map((health) => {
      const healthLabel = Object.keys(PROJECT_HEALTH).find(
        (key) => PROJECT_HEALTH[key as keyof typeof PROJECT_HEALTH] === health
      );
      return {
        key: 'health',
        value: health,
        label: intl.get(`PROJECT_DETAIL.HEALTH_OPTIONS.${healthLabel}`),
      };
    });
  }, [appliedFilters.health, isConsolidatedView]);

  const appliedProcessFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.process) return [];
    return selectedProcesses.map((process: ProjectProcess) => ({
      key: 'process',
      value: process.id,
      label: process.name,
    }));
  }, [appliedFilters.process, isConsolidatedView, selectedProcesses]);

  const appliedStageFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.stage) return [];
    return selectedStages.map((stage: ProcessStage) => ({
      key: 'stage',
      value: stage.id,
      label: stage.name,
    }));
  }, [appliedFilters.stage, isConsolidatedView, selectedStages]);

  const appliedCategoriesFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.category) return [];
    return selectedCategories.map((category: ProjectCategory) => ({
      key: 'category',
      value: category.id,
      label: category.categoryName,
    }));
  }, [appliedFilters.category, isConsolidatedView, selectedCategories]);

  const appliedArchivedFilter = useMemo(() => {
    if (!isConsolidatedView) return [];
    if (!appliedFilters.isArchived) return [];
    return [
      {
        key: 'isArchived',
        value: appliedFilters.isArchived.toString(),
        label: intl.get('PROJECT_DETAIL.ARCHIVED'),
      },
    ];
  }, [appliedFilters.isArchived, isConsolidatedView]);

  const handleRemoveFilter = useCallback(
    (filter: AppliedFilter) => {
      const copiedFilters = { ...appliedFilters };
      const copiedFilter =
        copiedFilters[filter.key as keyof ProjectsListFilters];
      if (Array.isArray(copiedFilter)) {
        const filteredFilter = copiedFilter.filter(
          (item) => item !== filter.value
        );
        copiedFilters[filter.key as keyof ProjectsListFilters] = filteredFilter;
      } else {
        copiedFilters[filter.key as keyof ProjectsListFilters] = undefined;
      }
      applyFilters(copiedFilters);
    },
    [appliedFilters, applyFilters]
  );

  useEffect(() => {
    if (shouldFetchProjectOwners) {
      fetchSelectedProjectOwners(appliedFilters.owners as string[]);
    }
    if (isConsolidatedView && shouldFetchLdTeams) {
      fetchSelectedLdTeams(appliedFilters.teams as string[]);
    }
    if (isConsolidatedView && shouldFetchBusinessTeams) {
      fetchSelectedBusinessTeams(appliedFilters.businessTeams as string[]);
    }
    if (isConsolidatedView && shouldFetchProcesses) {
      fetchSelectedProcesses(appliedFilters.process as string[]);
    }
    if (isConsolidatedView && shouldFetchStages) {
      fetchSelectedStages(appliedFilters.stage as string[]);
    }
    if (!isConsolidatedView || !shouldFetchPrograms) {
      lastProgramsFilterFetchRef.current = null;
    } else if (lastProgramsFilterFetchRef.current !== programsFilterKey) {
      lastProgramsFilterFetchRef.current = programsFilterKey;
      fetchProjectPrograms();
    }
  }, [
    appliedFilters,
    fetchSelectedProjectOwners,
    shouldFetchProjectOwners,
    shouldFetchLdTeams,
    fetchSelectedProcesses,
    shouldFetchProcesses,
    fetchSelectedLdTeams,
    shouldFetchBusinessTeams,
    fetchSelectedBusinessTeams,
    isConsolidatedView,
    fetchSelectedStages,
    shouldFetchStages,
    shouldFetchPrograms,
    programsFilterKey,
    fetchProjectPrograms,
  ]);

  return (
    <div>
      <AppliedFilters<AppliedFilter>
        filters={[
          ...appliedProjectOwnersFilter,
          ...appliedStatusFilter,
          ...appliedLdTeamsFilter,
          ...appliedBusinessTeamsFilter,
          ...appliedProgramsFilter,
          ...appliedWithoutProgramsFilter,
          ...appliedDateRangeFilters,
          ...appliedResourcingTypeFilter,
          ...appliedPrivacyFilter,
          ...appliedPriorityFilter,
          ...appliedHealthFilter,
          ...appliedProcessFilter,
          ...appliedStageFilter,
          ...appliedCategoriesFilter,
          ...appliedArchivedFilter,
        ]}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={clearFilters}
        title={intl.get('FILTER_PANEL.FILTERS_APPLIED')}
        clearAllLabel={intl.get('FILTER_PANEL.CLEAR_ALL')}
        aria-label={`${tableName}__applied-filters`}
      />
    </div>
  );
};

export default AppliedProjectsTableFilters;
